import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useNavigation } from '../../contexts/NavigationContext';
import { useAuth } from '../../hooks/useAuth';
import { extractJobIdFromSlug } from '../../utils/slugify';
import Icon from '../AppIcon';

const BreadcrumbNavigation = ({ customBreadcrumbs = null }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { navigationPreferences } = useNavigation();
  const { userProfile } = useAuth();

  // Don't show breadcrumbs if disabled in preferences
  if (!navigationPreferences.showBreadcrumbs) {
    return null;
  }

  // Determine user role for dynamic parent assignment
  const userRole = userProfile?.role || 'va';
  const dashboardPath = userRole === 'employer' ? '/employer-dashboard' : '/va-dashboard';

  // Route mapping for breadcrumb generation
  const routeMap = {
    '/va-dashboard': { label: 'Dashboard', icon: 'LayoutDashboard' },
    '/employer-dashboard': { label: 'Dashboard', icon: 'LayoutDashboard' },
    '/applications': { label: 'Applications', icon: 'FileText', parent: '/va-dashboard' },
    '/job-search': { label: 'Job Search', icon: 'Search', parent: '/va-dashboard' },
    '/saved-jobs': { label: 'Saved Jobs', icon: 'Bookmark', parent: '/job-search' },
    '/job-details': { label: 'Jobs', icon: 'FileText', parent: '/job-search' }, // Changed parent from /va-dashboard to /job-search
    '/job-application': { label: 'Apply', icon: 'Send', parent: '/job-details' },

    '/job-posting': { label: 'Post Job', icon: 'Plus', parent: '/employer-dashboard' },
    '/candidate-search': { label: 'Find Candidates', icon: 'Users', parent: '/employer-dashboard' },
    '/messages-communication': { label: 'Messages', icon: 'MessageSquare' },
    '/messages': { label: 'Messages', icon: 'MessageSquare', parent: dashboardPath },
    '/interviews': { label: 'Interviews', icon: 'Calendar', parent: '/va-dashboard' },
    '/interview-management': { label: 'Interview Management', icon: 'Calendar', parent: '/employer-dashboard' },
    '/analytics': { label: 'Analytics', icon: 'BarChart3', parent: '/va-dashboard' },
    '/advanced-analytics': { label: 'Analytics', icon: 'BarChart3', parent: '/employer-dashboard' },
    '/va-profile': { label: 'My Profile', icon: 'User', parent: '/va-dashboard' },
    '/va-profile-settings': { label: 'Profile Settings', icon: 'Settings', parent: '/va-profile' },
    '/va-portfolio-management': { label: 'Portfolio Management', icon: 'Folder', parent: '/va-profile' },
    '/employer-profile-settings': { label: 'Company Settings', icon: 'Building', parent: '/employer-dashboard' },
    '/skills-assessment': { label: 'Skills Assessment', icon: 'Award', parent: '/va-dashboard' },
    '/team-management': { label: 'Team Management', icon: 'Users', parent: '/employer-dashboard' },
    '/auto-matching': { label: 'AI Matching', icon: 'Zap', parent: '/employer-dashboard' },
    '/enterprise-features': { label: 'Enterprise Features', icon: 'Star', parent: '/employer-dashboard' },
    '/contact-va': { label: 'Contact VA', icon: 'Mail', parent: '/candidate-search' },
    // Additional employer workflow routes
    '/job-edit': { label: 'Edit Job', icon: 'Edit', parent: '/employer-dashboard' },
    '/application-review': { label: 'Application Review', icon: 'FileText', parent: '/employer-dashboard' },
    '/va-profile-view': { label: 'VA Profile', icon: 'User', parent: '/candidate-search' },
    '/interview-schedule': { label: 'Schedule Interview', icon: 'Calendar', parent: '/interview-management' },
    '/employer-activities': { label: 'Activity History', icon: 'Clock', parent: '/employer-dashboard' },
    // Shared routes
    '/help-support': { label: 'Help & Support', icon: 'HelpCircle' },
    // Admin routes
    '/admin-moderation': { label: 'Admin Panel', icon: 'Shield' },
    '/admin-moderation/skills-assessment': { label: 'Skills Assessment Admin', icon: 'Award', parent: '/admin-moderation' },
    '/admin/upgrade-analytics': { label: 'Upgrade Analytics', icon: 'TrendingUp', parent: '/admin-moderation' }
  };

  // Generate breadcrumb path
  const generateBreadcrumbs = () => {
    if (customBreadcrumbs) {
      return customBreadcrumbs;
    }

    const pathSegments = location.pathname.split('/').filter(Boolean);
    const breadcrumbs = [];
    
    // Handle dynamic routes (e.g., /job-details/:id)
    let currentPath = '';
    for (let i = 0; i < pathSegments.length; i++) {
      currentPath += '/' + pathSegments[i];
      
      // Check if this is a dynamic route parameter (UUID or slug)
      const isUUID = /^[a-f0-9-]{36}$/.test(pathSegments[i]);
      const isNumeric = /^\d+$/.test(pathSegments[i]);
      const isSlug = pathSegments[i].includes('-') && extractJobIdFromSlug(pathSegments[i]);
      
      if ((isUUID || isNumeric || isSlug) && i > 0) {
        // This is likely an ID parameter or slug, use the parent route info
        const parentPath = currentPath.substring(0, currentPath.lastIndexOf('/'));
        const routeInfo = routeMap[parentPath];
        if (routeInfo) {
          let label = `${routeInfo.label} Details`;
          
          // Handle specific dynamic routes
          if (parentPath === '/job-details' && isSlug) {
            // Extract job title from slug and remove any trailing IDs (like f0dcb8b0)
            const slug = pathSegments[i];
            const parts = slug.split('-');
            
            // Check if the last part looks like a short ID (8 characters, hexadecimal)
            const lastPart = parts[parts.length - 1];
            const isShortId = lastPart && lastPart.length === 8 && /^[0-9a-f]+$/i.test(lastPart);
            
            // If last part is an ID, remove it for the title
            const titleParts = isShortId ? parts.slice(0, parts.length - 1) : parts;
            const jobTitle = titleParts
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ');
              
            if (jobTitle) {
              label = jobTitle;
            }
          } else if (parentPath === '/contact-va') {
            label = 'Contact VA';
          } else if (parentPath === '/job-edit') {
            label = 'Edit Job';
          } else if (parentPath === '/application-review') {
            label = 'Review Application';
          } else if (parentPath === '/va-profile-view') {
            label = 'VA Profile';
          } else if (parentPath === '/interview-schedule') {
            label = 'Schedule Interview';
          }
          
          breadcrumbs.push({
            label,
            path: currentPath,
            icon: routeInfo.icon,
            isActive: i === pathSegments.length - 1
          });
        }
      } else {
        const routeInfo = routeMap[currentPath];
        if (routeInfo) {
          breadcrumbs.push({
            label: routeInfo.label,
            path: currentPath,
            icon: routeInfo.icon,
            isActive: i === pathSegments.length - 1
          });
        }
      }
    }

    // Add parent breadcrumbs if missing
    const completeBreadcrumbs = [];
    breadcrumbs.forEach(crumb => {
      const routeInfo = routeMap[crumb.path];
      if (routeInfo && routeInfo.parent) {
        const parentInfo = routeMap[routeInfo.parent];
        if (parentInfo && !completeBreadcrumbs.find(c => c.path === routeInfo.parent)) {
          completeBreadcrumbs.push({
            label: parentInfo.label,
            path: routeInfo.parent,
            icon: parentInfo.icon,
            isActive: false
          });
        }
      }
      completeBreadcrumbs.push(crumb);
    });

    return completeBreadcrumbs.filter(crumb => 
      crumb && 
      typeof crumb.path === 'string' && 
      typeof crumb.label === 'string' && 
      crumb.path.length > 0 && 
      crumb.label.length > 0
    );
  };

  const breadcrumbs = generateBreadcrumbs();

  if (breadcrumbs.length <= 1) {
    return null; // Don't show breadcrumbs for single-level pages
  }

  const handleBreadcrumbClick = (path) => {
    // Special handling for job-details without an ID
    if (path === '/job-details') {
      // Redirect to job search instead of non-existent /job-details route
      navigate('/job-search');
      return;
    }
    
    navigate(path);
  };

  return (
    <div className="bg-gray-50 border-b border-gray-200 px-4 sm:px-6 lg:px-8">
      <div className="content-container max-w-7xl mx-auto">
        <nav 
          className="flex items-center space-x-2 py-3 text-sm"
          data-testid="breadcrumb-navigation"
          aria-label="Breadcrumb"
        >
          {breadcrumbs
            .filter(crumb => 
              crumb && 
              crumb.path && 
              crumb.label && 
              typeof crumb.path === 'string' && 
              typeof crumb.label === 'string'
            )
            .map((crumb, index) => (
            <React.Fragment key={`breadcrumb-${crumb.path}-${index}`}>
              {index > 0 && (
                <Icon 
                  name="ChevronRight" 
                  size={14} 
                  className="text-gray-400 flex-shrink-0" 
                />
              )}
              
              <button
                onClick={() => !crumb.isActive && crumb.path && handleBreadcrumbClick(crumb.path)}
                className={`flex items-center space-x-1.5 px-2 py-1 rounded-md ${
                  crumb.isActive
                    ? 'text-gray-900 font-medium bg-white shadow-sm cursor-default'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white hover:shadow-sm'
                }`}
                disabled={crumb.isActive || !crumb.path}
              >
                {crumb.icon && typeof crumb.icon === 'string' && (
                  <Icon 
                    name={crumb.icon} 
                    size={14} 
                    className={crumb.isActive ? 'text-gray-700' : 'text-gray-500'} 
                  />
                )}
                <span className="whitespace-nowrap">
                  {typeof crumb.label === 'string' ? crumb.label : 'Page'}
                </span>
              </button>
            </React.Fragment>
          ))}
        </nav>
      </div>
    </div>
  );
};

export default BreadcrumbNavigation;
